import axios from 'axios';

export type InsightsHubSortBy = 'relevant' | 'recent';

/**
 * `category_labels` carries the human category name, and the group label for
 * categories the UI presents as one row ("News & events" spans the `news` and
 * `events` slugs). Searching it is what lets a pill do nothing but prefill the
 * box: the phrase matches every document in the group because the whole phrase
 * is a value on each of them, so no slug mapping is needed on this side.
 */
const QUERY_BY_FIELDS = ['title', 'category', 'category_labels', 'content', 'summary', 'preview'];

// Keyed by field rather than positional, so dropping a field cannot desync the
// weights from it. Typesense rejects the search when the counts disagree.
const FIELD_WEIGHTS: Record<string, string> = {
  title: '5',
  category: '3',
  category_labels: '3',
  content: '1',
  summary: '1',
  preview: '1',
};

/**
 * The field only exists once the reindex Lambda has run. Until then Typesense
 * answers a query naming it with 404 "Could not find a field named", which
 * would take research-hub search down completely rather than degrade it. This
 * lets the frontend ship before the reindex, in either order.
 */
const isMissingFieldError = (error: unknown) => {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;
  return typeof message === 'string' && message.includes('Could not find a field named');
};

const SORT_BY_MAP: Record<InsightsHubSortBy, string> = {
  // Coarse relevance buckets, then articles above research updates, then newer first.
  relevant: '_text_match(buckets: 10):desc,_eval(type:article):desc,published_date:desc',
  recent: 'published_date:desc',
};

type Options = {
  q: string;
  query_by?: string[];
  page?: number;
  per_page?: number;
  projectTypes?: string[];
  sortBy?: InsightsHubSortBy;
  /** Drops research updates, which otherwise outrank articles by date. */
  onlyArticles?: boolean;
};

export async function makeIndexedInsightsHubSearchRequest({
  q,
  page,
  per_page,
  query_by,
  projectTypes,
  sortBy,
  onlyArticles,
}: Options) {
  const endpoint =
    process.env.IS_PRODUCTION_APP === 'true'
      ? '/collections/articles/documents/search/'
      : '/collections/articles-draft/documents/search/';

  const payload: Record<string, string> = {
    q,
    query_by: query_by?.join(',') ?? QUERY_BY_FIELDS.join(','),
    per_page: `${per_page ?? '20'}`,
    page: `${page ?? '1'}`,
    sort_by: SORT_BY_MAP[sortBy ?? 'relevant'],
    prioritize_exact_match: 'true',
    // Without this, every token in a multi-word query has to match: "News
    // Events" found 4 of 343 documents while "news" alone found 78. Sending it
    // explicitly re-enables the progressive token dropping, so a sparse query
    // widens (62) and the sort_by above still floats full-token matches.
    drop_tokens_threshold: '10',
  };

  const filters: string[] = [];

  // Website-only posts must never appear in platform search. This is applied
  // unconditionally rather than in one arm of the Compass branch, which is how the
  // previous guard came to cover non-Compass users only and was then removed
  // wholesale when it broke their search. Requires the indexer to populate
  // is_public_only (serverless-functions #6); before that reindex this matches
  // nothing and search returns empty.
  filters.push('is_public_only:false');

  if (projectTypes && projectTypes.length > 0) {
    // Match Strapi: (visibility != subscriber_only) OR (subscriber_only with null/matching project_types)
    // Using num_project_types:=0 to check for empty project_types array
    const projectTypesStr = projectTypes.map((type) => `"${type}"`).join(',');
    // The visibility filter mixes || and &&, so it is parenthesised before the
    // rest is anded on.
    filters.push(
      `(is_subscriber_only:false || (is_subscriber_only:true && (num_project_types:=0 || project_type:[${projectTypesStr}])))`
    );
  }

  if (onlyArticles) {
    filters.push('type:=article');
  }

  if (filters.length) {
    payload.filter_by = filters.join(' && ');
  }

  const fields = query_by ?? QUERY_BY_FIELDS;

  const send = async (searchFields: string[]) => {
    const params = new URLSearchParams({
      ...payload,
      query_by: searchFields.join(','),
      // Only sent for the default field list; a caller passing its own fields
      // gets Typesense's default weighting.
      ...(query_by
        ? {}
        : { query_by_weights: searchFields.map((f) => FIELD_WEIGHTS[f] ?? '1').join(',') }),
    }).toString();
    return axios.get(`${process.env.TYPESENSE_URL}${endpoint}?${params}`, {
      headers: { 'X-TYPESENSE-API-KEY': process.env.TYPESENSE_API_KEY },
    });
  };

  let res;
  try {
    res = await send(fields);
  } catch (error) {
    const withoutNewField = fields.filter((field) => field !== 'category_labels');
    if (!isMissingFieldError(error) || withoutNewField.length === fields.length) throw error;
    res = await send(withoutNewField);
  }

  // Typesense reports the full match count in `found`; the hits array is only
  // the current page, so a caller showing "N results" needs both.
  return { hits: res.data?.hits ?? [], found: res.data?.found ?? 0 };
}
